import { useState, useEffect } from 'react';
import { ListPage, Badge, Person, Notice, useToast } from '@kollez/design-system';
import { apiGet, apiPost } from '@/lib/api';

interface Candidate {
  _id: string;
  user: { fullName: string; email: string; phone: string; createdAt: string };
  academic?: { rollNumber?: string; batchYear?: number };
  provenance: {
    selfDeclaredRollNumber?: string;
    selfDeclaredBatchYear?: number;
    declaredAt: string;
  };
}

/**
 * TPO claim queue — lists Persona-2 direct-signup students who declared this TPO's college
 * during registration but haven't been claimed yet (§27.5). Backed by
 * `GET /api/v1/students/claim-candidates` + `POST /api/v1/students/:id/claim`.
 */
export function TpoClaimQueuePage() {
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    setLoading(true);
    try {
      const r = await apiGet<{ candidates: Candidate[] }>('/students/claim-candidates');
      setCandidates(r.candidates ?? []);
    } catch (err) {
      toast.show({ tone: 'warning', message: 'Failed to load queue' });
    } finally {
      setLoading(false);
    }
  }

  async function claim(c: Candidate, reason?: string) {
    setClaiming(c._id);
    try {
      await apiPost(`/students/${c._id}/claim`, { reason });
      toast.show({
        tone: 'success',
        title: 'Claimed',
        message: `${c.user.fullName} is now in your college. They can apply to your drives.`,
      });
      setCandidates((prev) => prev.filter((x) => x._id !== c._id));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
        ?? 'Claim failed';
      toast.show({ tone: 'warning', title: 'Claim failed', message: msg });
    } finally {
      setClaiming(null);
    }
  }

  return (
    <>
      <Notice tone="info" className="mb-4">
        <b>Persona-2 signups</b>: students who registered on Kollez directly and told us they're at your
        college. Confirm they're yours before they can apply to your drives.
      </Notice>

      <ListPage<Candidate>
        eyebrow="Claim queue"
        title="Students who say they're at your college"
        subtitle={`${candidates.length} waiting for you to confirm. Claim them one-by-one or bulk after a spot check.`}
        actions={[{ label: 'Refresh', onClick: refresh }]}
        // Deep-audit fix (Page 37/N): three of the four KPIs were fabricated
        // placeholders — 'Auto-matched on import' hardcoded to '—' with a
        // runs-later meta, 'Total claimed' hardcoded to '—' with a
        // since-launch meta, 'Rejected' hardcoded to 0. No API returned any
        // of them. A TPO scanning the strip would read '0 rejected' as
        // authoritative when it wasn't. Cut the strip down to the one
        // number we actually know: waiting.
        kpis={[
          { label: 'Waiting', value: candidates.length, tone: candidates.length ? 'warning' : 'success' },
        ]}
        search={{ placeholder: 'Search by name, roll, email', keys: ['_id'] }}
        rows={candidates}
        rowKey={(r) => r._id}
        columns={[
          { key: 'p', label: 'Student', render: (r) => <Person name={r.user.fullName} sub={`${r.user.email} · ${r.user.phone}`} /> },
          {
            key: 'roll', label: 'Declared roll',
            render: (r) => r.provenance.selfDeclaredRollNumber ?? <span className="text-ink-tertiary">—</span>,
          },
          {
            key: 'batch', label: 'Batch', right: true,
            render: (r) => r.provenance.selfDeclaredBatchYear ?? <span className="text-ink-tertiary">—</span>,
          },
          {
            key: 'age', label: 'Signed up', hideMobile: true,
            render: (r) => new Date(r.provenance.declaredAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
          },
          {
            key: 'act', label: '', right: true,
            render: (r) => (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={claiming === r._id}
                onClick={(e) => { e.stopPropagation(); void claim(r); }}
              >
                {claiming === r._id ? 'Claiming…' : 'Claim'}
              </button>
            ),
          },
        ]}
        drawer={(r) => ({
          eyebrow: 'Direct signup',
          title: r.user.fullName,
          body: (
            <div className="grid gap-3">
              <div className="text-sm text-ink-secondary">
                {r.user.email} · {r.user.phone}
              </div>
              <div className="grid gap-1 text-sm">
                <div className="flex justify-between"><span className="text-ink-tertiary">Declared roll</span><b>{r.provenance.selfDeclaredRollNumber ?? '—'}</b></div>
                <div className="flex justify-between"><span className="text-ink-tertiary">Declared batch</span><b>{r.provenance.selfDeclaredBatchYear ?? '—'}</b></div>
                <div className="flex justify-between"><span className="text-ink-tertiary">Signed up</span><b>{new Date(r.provenance.declaredAt).toLocaleDateString('en-IN')}</b></div>
              </div>
              <Notice tone="info">
                Once claimed, their tenant becomes your college and they can apply to your drives.
                Nothing else on their profile changes — skills, resume, projects survive intact.
              </Notice>
              {/* Deep-audit fix (Page 37/N): 'Not our student' had no
                  onClick and no API. A TPO clicking to reject a
                  fraudulent Persona-2 claim expected it to remove the
                  row and log the rejection; instead nothing happened.
                  There is no /students/:id/reject-claim endpoint today.
                  Disabled with a 'soon' label pointing at the workaround
                  (leaving the row unclaimed drops the student off the
                  queue after the /students/claim-candidates read filters
                  by declaredAt — but that's a workaround, not a proper
                  rejection audit trail). */}
              <div className="flex gap-2">
                <button className="btn btn-primary" onClick={() => void claim(r)} disabled={claiming === r._id}>
                  Claim into my college
                </button>
                <button
                  className="btn btn-secondary"
                  disabled
                  title="Explicit rejection ships in the next release. Skip unclaimed rows for now — they stay in the queue."
                >
                  Not our student · soon
                </button>
              </div>
            </div>
          ),
        })}
        empty={{
          title: loading ? 'Loading…' : 'Queue is empty',
          hint: loading ? 'Fetching your candidates.' : 'No direct-signup students named your college recently.',
        }}
      />
    </>
  );
}
